// src/layouts/scenarios/create/functions/fetchRoutes.js
import axios from "axios";
import config from "config";

const API_URL = config.TRAFFIC;
let routesCache = null;
let routesPromise = null;

// Color logic based on speed limit and average speed
const setColorLine = (limit, avgSpeed) => {
  const rgbColor = {
    green: "#12B51A",
    orange: "#E98500",
    red: "#C74811",
    darkRed: "#970029",
  };

  const thresholds = {
    130: { green: 91, orange: 52, red: 26 },
    90: { green: 63, orange: 36, red: 18 },
    85.4: { green: 58, orange: 34, red: 17 },
    84: { green: 58, orange: 34, red: 17 },
    70: { green: 49, orange: 28, red: 14 },
    60: { green: 42, orange: 24, red: 12 },
    50: { green: 36, orange: 21, red: 11 },
    40: { green: 28, orange: 16, red: 8 },
    36.8: { green: 24, orange: 14, red: 7 },
    35.9: { green: 24, orange: 14, red: 7 },
    35.8: { green: 24, orange: 14, red: 7 },
    36: { green: 24, orange: 14, red: 7 },
    29: { green: 20, orange: 12, red: 6 },
    20: { green: 14, orange: 8, red: 5 },
  };

  const t = thresholds[limit];

  if (!t) return rgbColor.darkRed;

  if (avgSpeed > t.green) {
    return rgbColor.green;
  } else if (avgSpeed > t.orange) {
    return rgbColor.orange;
  } else if (avgSpeed > t.red) {
    return rgbColor.red;
  } else {
    return rgbColor.darkRed;
  }
};

function formatMinuteDotSecond(value) {
  if (value == null || isNaN(value)) return "";

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const [minutePart, secondPart] = absValue.toString().split(".");
  const totalMinutes = parseInt(minutePart, 10);
  const seconds = secondPart ? parseInt(secondPart.padEnd(2, "0"), 10) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = isNegative ? "-" : "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0 || hours > 0) result += `${minutes} min `;
  result += `${seconds} sec`;

  return result.trim();
}

const formatRouteHoverMessage = (props = {}) => {
  const avgSpeed = props.avg_speed_paths;
  const delay = formatMinuteDotSecond(props.delay);
  const ttPaths = formatMinuteDotSecond(props.tt_paths);

  const timeRaw = props.timestamp;
  const timeStr = timeRaw
    ? new Date(timeRaw).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  const avgSpeedStr =
    avgSpeed !== null && avgSpeed !== undefined
      ? `${Number(avgSpeed).toFixed(2)} km/h`
      : "-";

  return `
    <div style="min-width:220px">
      <div style="font-weight:600; margin-bottom:6px">
        Average Speed: <span style="font-weight:400">${avgSpeedStr}</span>
      </div>
      <div><strong>Delay:</strong> ${delay || "-"}</div>
      <div><strong>Travel Time:</strong> ${ttPaths || "-"}</div>
      <div style="margin-top:6px; font-size:12px; opacity:0.8">
        <strong>Time:</strong> ${timeStr}
      </div>
           <div><strong>Id_Path:</strong> ${props.id_path || "-"}</div>
            <div><strong>Id_Route:</strong> ${props.id_route || "-"}</div>
<div><strong>Id_DriveThess:</strong> ${props.id_drivethess_paths || "-"}</div>            
    </div>
  `;
};

// MultiLineString → LineString (flatten)
function normalizeFeatureGeometry(feature) {
  const geom = feature.geometry;
  if (!geom) return null;

  if (geom.type === "MultiLineString") {
    const flat = geom.coordinates.flat();
    return {
      type: "LineString",
      coordinates: flat,
    };
  }

  if (geom.type === "LineString") {
    return geom;
  }

  return null;
}
//30, 27, 22,29,4,25,5,2
const excludedPathIds = new Set([]);
// Collect segments, merged geometry and linkIds for a specific route_id
function buildRouteSegmentsForId(geomFeatures, routeId) {
  // const segments = geomFeatures.filter(
  //   (f) => f.properties?.route_id === routeId
  // );
  const segments = geomFeatures.filter(
  (f) =>
    f.properties?.route_id === routeId &&
    !excludedPathIds.has(Number(f.properties?.id_path))
);

  if (!segments.length) {
    return {
      segments: [],
      mergedGeometry: null,
      linkIds: [],
    };
  }

  const allCoords = [];
  const linkIdsSet = new Set();

  segments.forEach((f) => {
    const g = normalizeFeatureGeometry(f);
    if (g && Array.isArray(g.coordinates)) {
      allCoords.push(...g.coordinates);
    }

    const idPath = f.properties?.id_path;
    if (idPath !== null && idPath !== undefined) {
      linkIdsSet.add(idPath);
    }
  });

  const mergedGeometry =
    allCoords.length > 0
      ? {
          type: "LineString",
          coordinates: allCoords,
        }
      : null;

  return {
    segments,
    mergedGeometry,
    linkIds: [...linkIdsSet],
  };
}

export async function fetchRoutes() {
  if (routesCache) return routesCache;
  if (routesPromise) return routesPromise;

  routesPromise = Promise.all([
    axios.get(`${API_URL}/id_routes`, {
      headers: { "Accept-Profile": "traffic_monitoring" },
    }),
    axios.get(`${API_URL}/rpc/traffic_monitoring_geom`, {
      headers: { "Accept-Profile": "traffic_monitoring" },
    }),
  ])
    .then(([routesRes, geomRes]) => {
      const routeList = routesRes.data || [];
      const geomRaw = geomRes.data || [];
      const geomFeatures = Array.isArray(geomRaw)
        ? geomRaw
        : geomRaw.features || [];

      routesCache = routeList.map((route) => {
        const rid = route.route_id;

        const { segments, mergedGeometry, linkIds } = buildRouteSegmentsForId(
          geomFeatures,
          rid
        );

        const links = linkIds
          .map((linkId) => {
            const feature = segments.find((f) => f.properties?.id_path === linkId);
            if (!feature) return null;

            const geom = normalizeFeatureGeometry(feature);
            if (!geom) return null;

            const props = feature.properties || {};
            const limit = props.speed_limit;
            const avgSpeed = props.avg_speed_paths;

            const color = setColorLine(limit, avgSpeed);

            return {
              id: linkId,
              type: "Feature",
              geometry: geom,
              properties: {
                ...props,
                color,
                hoverMessage: formatRouteHoverMessage(props),
              },
            };
          })
          .filter(Boolean);

        return {
          id: rid,
          name: route.name || `Route ${rid}`,
          linkIds,
          links,
          raw: route,
          mergedGeometry,
        };
      });

      routesPromise = null;
      return routesCache;
    })
    .catch((error) => {
      routesPromise = null;
      throw error;
    });

  return routesPromise;
}
